import { computed, onBeforeUnmount, onDeactivated, ref, shallowRef, unref } from 'vue'
import { addDaysYmd, diffDaysYmd, shiftAgendaItemDays, ymd } from '@/utils/agendaItem'
import { WEEK_DAYS } from '@/composables/useMonthGrid'

/**
 * 월 그리드에서 칩을 끌어 날짜를 옮긴다.
 *
 * ── 이 구현의 핵심: 고스트가 아니라 **다시 배치한다** ──
 *
 * 흔한 방식은 커서를 따라다니는 반투명 복제본(고스트)을 그리고, 놓을 칸에
 * 테두리를 두르는 것이다. 그런데 월 그리드에서 "어느 칸인가" 는 절반의 정보다.
 * 한 칸에는 레인이 여러 줄 있고, 어느 줄에 꽂힐지는 그 날의 다른 항목들과
 * 유형 우선순위(useMonthGrid 의 정렬 규칙)가 함께 결정한다. 칸만 칠해 주면
 * 사용자는 놓아 본 뒤에야 실제 위치를 안다.
 *
 * 그래서 여기서는 **끌고 있는 항목만 날짜를 옮긴 목록**을 만들어 그리드에
 * 그대로 먹인다. 패킹이 다시 돌면서
 *   - 옮겨진 칩이 최종 레인에 자리를 잡고
 *   - 그 자리를 비켜 주려고 다른 칩들이 실제로 밀린다
 * 즉 화면에 보이는 것이 곧 놓았을 때의 결과다. 플레이스홀더를 따로 그릴
 * 필요가 없다 — 옮겨진 칩 자체가 플레이스홀더다(점선 테두리로 "아직 확정
 * 아님" 만 표시한다).
 *
 * ── 좌표 → 날짜 ──
 *
 * elementFromPoint 는 쓸 수 없다. 칩이 날짜 버튼을 덮고 있어서 커서 아래
 * 요소가 칸이 아니라 칩인 경우가 대부분이다. 대신 주 행(.umv__week)의
 * 사각형을 찾고 그 안에서 X 를 7 등분한다. 컬럼은 repeat(7, 1fr) 이라
 * 등분이 정확하고, 셀 사이 1px 테두리에 커서가 걸려도 빈틈이 생기지 않는다.
 *
 * ── 드래그가 "살아남지" 않게 하는 것 ──
 *
 * pointerup 은 놓칠 수 있다. Alt+Tab 이나 네이티브 모달처럼 포커스가 통째로
 * 넘어가면 암묵 포인터 캡처가 끊기고, 버튼을 뗀 사건이 페이지에 도착하지
 * 않는다. 회복 경로가 없으면 드래그가 무장된 채로 남아서 — 커서를 따라
 * 칩이 계속 움직이고 — **다음 클릭 한 번이 조용히 날짜를 바꾼다.**
 * 그래서 세 겹으로 막는다: pointerId 대조, buttons===0 감지, window blur /
 * 탭 숨김. (setPointerCapture 를 칩에 걸 수는 없다. 스팬이 주 경계를 넘으면
 * Vue 가 그 DOM 을 새로 만들어 캡처가 사라진다.)
 *
 * ── 터치는 제외한다 ──
 *
 * 칸반(TaskBoardView)과 같은 판단이다. 모바일 월 그리드의 칩은 높이 16px,
 * 칸 폭이 55px 남짓이라 잡을 만한 손잡이가 못 되고, 길게 눌러 끌기를 넣으면
 * 세로 스크롤 · 월 넘김 스와이프(useSwipeNavigation)와 같은 제스처를 두고
 * 다툰다. 모바일에서 날짜를 바꾸는 길은 칸을 탭해서 여는 일자 시트다.
 */

// 클릭과 드래그를 가르는 이동량. 칸반(TaskBoardView)과 같은 값을 쓴다.
const THRESHOLD_PX = 6
// 그리드 가장자리를 이만큼 벗어나도 마지막 칸으로 붙여 준다.
// 주 행 사이 1px 테두리와, 6주차 아래에서 손이 조금 흘러내리는 것을 흡수한다.
const EDGE_SLACK_PX = 24

function shortLabel(dateYmd) {
  const d = new Date(`${dateYmd}T00:00:00`)
  return `${d.getMonth() + 1}/${d.getDate()}(${WEEK_DAYS[d.getDay()]})`
}

/**
 * @param {object}   o
 * @param {Ref<HTMLElement>} o.rootRef  그리드 루트 (.umv)
 * @param {Ref<Date[][]>}    o.weeks    6주 × 7일. 항목과 무관하게 월만으로 정해진다.
 * @param {Ref<Array>}       o.items    정규화된 AgendaItem 원본 목록
 * @param {Ref<boolean>}     o.enabled  드래그 허용 여부(터치/읽기 전용에서 false)
 * @param {(p:{item:object, days:number}) => void} o.onMove  놓았을 때. 동기 구간에서
 *        낙관적 갱신을 끝내야 칩이 원위치로 한 번 튀지 않는다(아래 onPointerUp 주석).
 */
export function useMonthDragMove({ rootRef, weeks, items, enabled, onMove }) {
  // 임계값을 넘어 실제로 끌기 시작한 항목. 얕은 ref 로 둔다 — 항목 내부를
  // 감시할 이유가 없고, 목록 전체가 반응형으로 재귀 추적되면 손해만 본다.
  const dragItem = shallowRef(null)
  const grabYmd = ref(null)   // 집은 지점의 날짜 (스팬 중간을 집어도 어긋나지 않게)
  const deltaDays = ref(0)
  const outside = ref(false)  // 그리드 밖 — 놓아도 아무 일 없음
  const pointerX = ref(0)
  const pointerY = ref(0)

  let pending = null            // 임계값 전 후보 { item, grabYmd }
  let activePointerId = null    // 이 드래그를 시작한 포인터. 다른 포인터는 무시한다.
  let cancelled = false         // Escape 로 접었고, 뒤따르는 클릭만 기다리는 중
  let startX = 0
  let startY = 0
  let frame = 0
  let lastX = 0
  let lastY = 0
  let suppressClick = false

  const isDragging = computed(() => !!dragItem.value)
  const dragUid = computed(() => dragItem.value?.uid || null)

  /** 그리드가 그려야 할 목록. 드래그 중이 아니면 원본 그대로. */
  const previewItems = computed(() => {
    const it = dragItem.value
    if (!it) return null
    const days = deltaDays.value
    // 제자리면 **같은 배열 참조**를 돌려준다. 그래야 패킹 computed 가
    // 다시 돌지 않는다(Vue 는 === 로 변화를 판단한다).
    if (!days) return unref(items) || []
    return (unref(items) || []).map((x) => (x.uid === it.uid ? shiftAgendaItemDays(x, days) : x))
  })

  /** 항목이 덮게 될 날짜들. 칸 배경을 칠해 스팬 전체가 어디에 놓이는지 보여준다. */
  const targetDates = computed(() => {
    const it = dragItem.value
    if (!it || outside.value || !it.date) return null
    const from = addDaysYmd(it.date, deltaDays.value)
    const to = addDaysYmd(it.endDate || it.date, deltaDays.value)
    const set = new Set()
    let cur = from
    // 그리드는 42칸이지만 항목 자체는 그보다 길 수 있다. 상한을 둬서
    // 이상한 데이터(endDate < date)가 무한 루프가 되지 않게 한다.
    for (let i = 0; i < 400 && cur && cur <= to; i += 1) {
      set.add(cur)
      cur = addDaysYmd(cur, 1)
    }
    return set
  })

  /** 커서 옆 알약에 띄울 착지 날짜 */
  const dropLabel = computed(() => {
    const it = dragItem.value
    if (!it || outside.value || !it.date) return ''
    const from = addDaysYmd(it.date, deltaDays.value)
    const to = addDaysYmd(it.endDate || it.date, deltaDays.value)
    return from === to ? shortLabel(from) : `${shortLabel(from)} → ${shortLabel(to)}`
  })

  function canDrag(item) {
    return !!(unref(enabled) && item && item.canEdit)
  }

  /** 화면 좌표 → 'YYYY-MM-DD'. 그리드를 크게 벗어나면 null. */
  function resolveYmd(clientX, clientY) {
    const root = unref(rootRef)
    const grid = unref(weeks)
    if (!root || !grid || !grid.length) return null

    const rows = root.querySelectorAll('[data-week]')
    if (!rows.length) return null

    let bestEl = null
    let bestRect = null
    let bestDy = Infinity
    rows.forEach((el) => {
      const r = el.getBoundingClientRect()
      if (!r.height) return
      const dy = clientY < r.top ? r.top - clientY : clientY > r.bottom ? clientY - r.bottom : 0
      if (dy < bestDy) {
        bestDy = dy
        bestEl = el
        bestRect = r
      }
    })
    if (!bestEl || bestDy > EDGE_SLACK_PX) return null
    if (clientX < bestRect.left - EDGE_SLACK_PX || clientX > bestRect.right + EDGE_SLACK_PX) return null

    const col = Math.min(6, Math.max(0, Math.floor(((clientX - bestRect.left) / bestRect.width) * 7)))
    const row = grid[Number(bestEl.dataset.week)]
    return row ? ymd(row[col]) : null
  }

  function setDragStyles(on) {
    if (typeof document === 'undefined') return
    document.body.style.userSelect = on ? 'none' : ''
    document.body.style.webkitUserSelect = on ? 'none' : ''
    // 그리드 밖에서도 커서를 유지한다. 그리드 안은 CSS 쪽이 맡는다 —
    // 칩과 날짜 버튼이 각자 cursor 를 갖고 있어 body 값만으로는 안 이긴다.
    document.body.style.cursor = on ? 'grabbing' : ''
  }

  function apply() {
    if (!dragItem.value) return
    pointerX.value = lastX
    pointerY.value = lastY
    const drop = resolveYmd(lastX, lastY)
    if (!drop) {
      // 밖으로 나가면 **원래 자리로 되돌린다**. 마지막 유효 칸에 남겨 두면
      // 그리드 밖에서 손을 뗐을 때 의도하지 않은 날짜로 옮겨진다.
      outside.value = true
      deltaDays.value = 0
      return
    }
    outside.value = false
    deltaDays.value = diffDaysYmd(drop, grabYmd.value)
  }

  function schedule() {
    if (frame) return
    frame = requestAnimationFrame(() => {
      frame = 0
      apply()
    })
  }

  // ── 정리 ────────────────────────────────────────────────────────
  function detach() {
    document.removeEventListener('pointermove', onPointerMove)
    document.removeEventListener('pointerup', onPointerUp)
    document.removeEventListener('pointercancel', onPointerCancel)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('blur', onWindowBlur)
    if (frame) {
      cancelAnimationFrame(frame)
      frame = 0
    }
  }

  /** 화면 상태만 접는다(리스너는 유지) */
  function clearDragState() {
    pending = null
    dragItem.value = null
    grabYmd.value = null
    deltaDays.value = 0
    outside.value = false
    setDragStyles(false)
  }

  function reset() {
    detach()
    clearDragState()
    activePointerId = null
    cancelled = false
  }

  /** 드래그로 끝난 조작이 곧바로 클릭(상세 열기 / 일자 시트)으로 이어지지 않게 한다 */
  function armClickGuard() {
    suppressClick = true
    setTimeout(() => {
      suppressClick = false
    }, 0)
  }

  /** 사용자가 놓기도 전에 드래그가 깨진 경우 (포커스 상실, 버튼 유실 등) */
  function abort() {
    if (dragItem.value || cancelled) armClickGuard()
    reset()
  }

  // ── 이벤트 ──────────────────────────────────────────────────────
  function onPointerMove(e) {
    if (activePointerId !== null && e.pointerId !== activePointerId) return

    /*
     * 버튼이 이미 떼어진 뒤에 들어오는 이동은 드래그가 아니다.
     * pointerup 을 놓쳤다는 뜻이므로 여기서 접는다 — 이 검사가 없으면
     * 드래그가 무장된 채 남아 다음 클릭이 날짜를 바꿔 버린다.
     */
    if (e.buttons === 0) {
      abort()
      return
    }

    lastX = e.clientX
    lastY = e.clientY

    if (!dragItem.value) {
      if (!pending) return
      if (Math.abs(lastX - startX) + Math.abs(lastY - startY) < THRESHOLD_PX) return
      dragItem.value = pending.item
      grabYmd.value = pending.grabYmd
      deltaDays.value = 0
      outside.value = false
      setDragStyles(true)
    }
    schedule()
  }

  function onPointerUp(e) {
    if (e && activePointerId !== null && e.pointerId !== activePointerId) return

    const item = dragItem.value
    const days = deltaDays.value

    if (item && days) {
      /*
       * **순서가 중요하다.** onMove 를 먼저 부르고 나서 상태를 지운다.
       * 부모는 onMove 의 동기 구간에서 items 를 낙관적으로 갱신하는데,
       * 그 전에 reset() 을 해 버리면 미리보기가 사라지면서 칩이 한 프레임
       * 원래 날짜로 돌아갔다가 다시 옮겨진다. 같은 틱 안에서 둘 다 끝내면
       * Vue 가 한 번만 렌더하므로 깜빡임이 없다.
       * (반복 일정처럼 부모가 확인을 먼저 묻는 경로는 낙관적 갱신이 없다.
       *  그때는 원위치로 돌아가는 것이 맞는 동작이다 — 아직 확정이 아니다.)
       */
      onMove?.({ item, days })
      armClickGuard()
    } else if (cancelled) {
      // Escape 로 취소했다. 손을 뗄 때 따라오는 클릭까지 삼켜야
      // 취소가 정말 "아무 일도 없음" 으로 끝난다.
      armClickGuard()
    }
    /*
     * 제자리 드롭(days === 0)에서는 클릭을 막지 않는다.
     * 임계값을 살짝 넘긴 손떨림까지 막으면 "칩을 눌렀는데 상세가 안 열린다"
     * 가 되는데, 그쪽이 훨씬 자주 일어나고 훨씬 나쁘다.
     */
    reset()
  }

  function onPointerCancel(e) {
    if (e && activePointerId !== null && e.pointerId !== activePointerId) return
    abort()
  }

  function onWindowBlur() {
    abort()
  }

  function onVisibilityChange() {
    if (document.hidden) abort()
  }

  function onKeyDown(e) {
    if (e.key !== 'Escape' || !dragItem.value) return
    e.preventDefault()
    // 상태만 접고 pointerup 은 계속 듣는다 — 버튼을 뗄 때 따라오는 클릭을
    // 삼켜야 취소가 "아무 일도 없음" 으로 끝난다.
    cancelled = true
    clearDragState()
  }

  function onChipPointerDown(e, item) {
    // 마우스/펜 왼쪽 버튼만. 터치는 스크롤과 스와이프에 양보한다.
    if (e.pointerType === 'touch' || e.button !== 0) return
    // 이미 하나를 끌고 있으면 두 번째 포인터는 무시한다.
    if (activePointerId !== null) return
    if (!canDrag(item)) return

    const g = resolveYmd(e.clientX, e.clientY)
    if (!g) return

    pending = { item, grabYmd: g }
    activePointerId = e.pointerId
    startX = e.clientX
    startY = e.clientY

    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp)
    document.addEventListener('pointercancel', onPointerCancel)
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('blur', onWindowBlur)
  }

  /** 루트에 캡처 단계로 달아 둔다 — 칩과 날짜 버튼의 클릭을 함께 막는다 */
  function onClickCapture(e) {
    if (!suppressClick) return
    e.stopPropagation()
    e.preventDefault()
    suppressClick = false
  }

  onBeforeUnmount(reset)
  // 캘린더는 keep-alive 대상이라 화면을 떠나도 unmount 되지 않는다.
  // 끌던 도중 탭을 옮기면 문서 리스너와 user-select 가 그대로 남는다.
  onDeactivated(reset)

  return {
    isDragging,
    dragUid,
    deltaDays,
    outside,
    pointerX,
    pointerY,
    previewItems,
    targetDates,
    dropLabel,
    canDrag,
    onChipPointerDown,
    onClickCapture
  }
}
